import { useMultiSelect } from "@/hooks/use-multi-select";
import { ResourceGalleryConfig, useResourceGallery } from "@/hooks/use-resource-gallery";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaChevronRight, FaSearch, FaTimes } from "react-icons/fa";

// =============================================================================
// Types
// =============================================================================

/**
 * Props for rendering a single item in the sidebar
 */
export interface SidebarItemRenderProps<T> {
  item: T;
  isSelected: boolean;
  orderedIds: string[];
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * Props for rendering a group header (optional custom rendering)
 */
export interface SidebarGroupRenderProps {
  groupKey: string;
  itemCount: number;
  isCollapsed: boolean;
  onToggle: () => void;
}

/**
 * Props for rendering a group with its items
 */
export interface SidebarGroupWithItemsRenderProps<T> {
  groupKey: string;
  items: T[];
  groupBy: string;
  orderedIds: string[];
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * Props for custom empty state rendering
 */
export interface EmptyStateProps {
  icon?: ReactNode;
  message: string;
  subMessage?: string;
}

/**
 * Props for the SidebarResourceSection component
 */
export interface SidebarResourceSectionProps<T> {
  /** Array of items to display */
  items: T[];
  /** Configuration for the resource gallery */
  config: ResourceGalleryConfig<T>;
  /** Render function for a single item */
  renderItem: (props: SidebarItemRenderProps<T>) => ReactNode;
  /** Render function for a group (replaces default group rendering) */
  renderGroup?: (props: SidebarGroupWithItemsRenderProps<T>) => ReactNode;
  /** Optional render function for group headers (used if renderGroup is not provided) */
  renderGroupHeader?: (props: SidebarGroupRenderProps) => ReactNode;
  /** Message to show when there are no items */
  emptyMessage: string;
  /** Icon for empty state */
  emptyIcon?: ReactNode;
  /** Sub-message for empty state */
  emptySubMessage?: string;
  /** Message to show when search returns no results */
  noResultsMessage?: string;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
  /** External groupBy value (from existing hooks like useTrackerGroupBy) */
  externalGroupBy?: string;
  /** External setGroupBy function (from existing hooks) */
  externalSetGroupBy?: (value: string) => void;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Custom loading component */
  loadingComponent?: ReactNode;
  /** Additional CSS class for the container */
  className?: string;
}

// =============================================================================
// Search Bar Component (Reusable)
// =============================================================================

export interface SidebarSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClear?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  isDisabled?: boolean;
}

/**
 * Reusable search bar component for sidebar sections
 */
export const SidebarSearchBar: React.FC<SidebarSearchBarProps> = ({
  value,
  onChange,
  placeholder = "Rechercher...",
  onClear,
  inputRef,
  isDisabled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const localRef = useRef<HTMLInputElement>(null);
  const searchRef = inputRef || localRef;

  const handleClear = useCallback(() => {
    onChange("");
    onClear?.();
    searchRef.current?.focus();
  }, [onChange, onClear, searchRef]);

  return (
    <div className="px-1 mb-3">
      <div
        className={`relative flex items-center rounded-md transition-all duration-200 ${isFocused
            ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
            : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
          } ${isDisabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        <FaSearch
          className={`absolute left-2.5 transition-colors duration-200 ${isFocused ? "arc-sidebar-icon-active" : ""
            }`}
          size={11}
        />
        <input
          ref={searchRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={isDisabled}
          className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
        />
        {value && (
          <button
            onClick={handleClear}
            className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
          >
            <FaTimes size={10} />
          </button>
        )}
      </div>
    </div>
  );
};

SidebarSearchBar.displayName = "SidebarSearchBar";

// =============================================================================
// Empty State Component
// =============================================================================

export interface SidebarEmptyStateProps {
  icon?: ReactNode;
  message: string;
  subMessage?: string;
  className?: string;
}

/**
 * Empty state component for sidebar sections
 */
export const SidebarEmptyState: React.FC<SidebarEmptyStateProps> = ({
  icon,
  message,
  subMessage,
  className = "",
}) => {
  return (
    <div className={`px-3 py-4 text-center text-[var(--arc-sidebar-text-secondary)] ${className}`}>
      {icon && (
        <div className="flex justify-center mb-2">
          <span className="text-2xl text-[var(--arc-sidebar-text-tertiary)] opacity-50">
            {icon}
          </span>
        </div>
      )}
      <span className="text-xs">{message}</span>
      {subMessage && (
        <p className="text-[10px] text-[var(--arc-sidebar-text-tertiary)] mt-1 opacity-70">
          {subMessage}
        </p>
      )}
    </div>
  );
};

SidebarEmptyState.displayName = "SidebarEmptyState";

// =============================================================================
// Loading State Component
// =============================================================================

export const SidebarLoadingState: React.FC<{ className?: string }> = ({ className = "" }) => {
  return (
    <div className={`px-2 py-4 ${className}`}>
      <div className="flex items-center justify-center py-8">
        <div className="animate-arc-spin w-6 h-6 border-2 border-[var(--arc-accent)] border-t-transparent rounded-full" />
      </div>
    </div>
  );
};

SidebarLoadingState.displayName = "SidebarLoadingState";

// =============================================================================
// Default Group Header Component
// =============================================================================

const DefaultGroupHeader: React.FC<SidebarGroupRenderProps> = ({
  groupKey,
  itemCount,
  isCollapsed,
  onToggle,
}) => {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] hover:bg-[var(--arc-sidebar-accent-glow)] rounded transition-colors"
    >
      {isCollapsed ? (
        <FaChevronRight size={10} className="text-[var(--arc-sidebar-text-tertiary)]" />
      ) : (
        <FaChevronDown size={10} className="text-[var(--arc-sidebar-text-tertiary)]" />
      )}
      <span className="flex-1 text-left truncate">{groupKey}</span>
      <span className="px-1.5 py-0.5 text-[10px] bg-[var(--arc-sidebar-accent-glow)] rounded">
        {itemCount}
      </span>
    </button>
  );
};

// =============================================================================
// Main Component
// =============================================================================

/**
 * SidebarResourceSection - Generic component for displaying resources in the sidebar.
 * Handles search, filtering, grouping with collapsible groups, and multi-select.
 *
 * This component is designed to be used for trackers, enrichments, alerts, etc.
 * in the Arc-style sidebar.
 */
function SidebarResourceSection<T>({
  items,
  config,
  renderItem,
  renderGroup,
  renderGroupHeader,
  emptyMessage,
  emptyIcon,
  emptySubMessage,
  noResultsMessage = "Aucun résultat",
  searchPlaceholder = "Rechercher...",
  onMultiSelectContextMenu,
  externalGroupBy,
  externalSetGroupBy,
  isLoading = false,
  loadingComponent,
  className = "",
}: SidebarResourceSectionProps<T>): React.ReactElement | null {
  // Multi-select hook
  const { isSelected } = useMultiSelect();

  // Use the resource gallery hook for search, filtering, and grouping
  const gallery = useResourceGallery(items, config, {
    externalGroupBy,
  });

  // Use external groupBy if provided, otherwise use the hook's internal state
  const effectiveGroupBy = externalGroupBy ?? gallery.groupBy;

  // Create a modified gallery state that uses external groupBy
  const modifiedGallery = useMemo(() => {
    if (!externalGroupBy) return gallery;

    // Recalculate grouped items with external groupBy
    const filteredItems = gallery.filteredItems;

    if (effectiveGroupBy === "none") {
      return {
        ...gallery,
        groupBy: effectiveGroupBy,
        groupedItems: { "": filteredItems },
      };
    }

    const groups: Record<string, T[]> = {};
    filteredItems.forEach((item) => {
      const groupKey = config.getGroupKey(item, effectiveGroupBy);
      const keys = Array.isArray(groupKey) ? groupKey : [groupKey];
      keys.forEach((key) => {
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
      });
    });

    // Sort keys
    const sortedKeys = Object.keys(groups).sort(
      config.sortGroups ||
      ((a, b) => {
        if (a === "Sans tags" || a === "Autre" || a === "Inactifs") return 1;
        if (b === "Sans tags" || b === "Autre" || b === "Inactifs") return -1;
        return a.localeCompare(b);
      })
    );

    const sortedGroups: Record<string, T[]> = {};
    sortedKeys.forEach((key) => {
      sortedGroups[key] = groups[key];
    });

    return {
      ...gallery,
      groupBy: effectiveGroupBy,
      groupedItems: sortedGroups,
    };
  }, [gallery, externalGroupBy, effectiveGroupBy, config]);

  const {
    searchTerm,
    setSearchTerm,
    filteredItems,
    groupedItems,
    collapsedGroups,
    toggleGroup,
    orderedIds,
  } = modifiedGallery;

  // Search input ref
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + F to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && items.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      // Escape to clear search
      if (e.key === "Escape" && isSearchFocused) {
        setSearchTerm("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, items.length, setSearchTerm]);

  // Track search focus state for keyboard handling
  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return;

    const handleFocus = () => setIsSearchFocused(true);
    const handleBlur = () => setIsSearchFocused(false);

    input.addEventListener("focus", handleFocus);
    input.addEventListener("blur", handleBlur);

    return () => {
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Loading state
  if (isLoading) {
    return loadingComponent ? (
      <>{loadingComponent}</>
    ) : (
      <SidebarLoadingState className={className} />
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <SidebarEmptyState
        icon={emptyIcon}
        message={emptyMessage}
        subMessage={emptySubMessage}
        className={className}
      />
    );
  }

  const hasContent = filteredItems.length > 0;
  const GroupHeaderComponent = renderGroupHeader || DefaultGroupHeader;

  return (
    <div className={`px-3 py-2 ${className}`}>
      {/* Search Bar */}
      <SidebarSearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={searchPlaceholder}
        inputRef={searchInputRef as React.RefObject<HTMLInputElement>}
      />

      {/* Grouped Items */}
      {hasContent && effectiveGroupBy !== "none" && (
        <div className="space-y-0.5">
          {Object.entries(groupedItems).map(([groupKey, groupItems]) => {
            // Use custom group renderer if provided
            if (renderGroup) {
              return (
                <React.Fragment key={groupKey}>
                  {renderGroup({
                    groupKey,
                    items: groupItems,
                    groupBy: effectiveGroupBy,
                    orderedIds,
                    onMultiSelectContextMenu,
                  })}
                </React.Fragment>
              );
            }

            // Default group rendering
            const isCollapsed = collapsedGroups.has(groupKey);

            return (
              <div key={groupKey} className="space-y-0.5">
                {/* Group Header */}
                <GroupHeaderComponent
                  groupKey={groupKey}
                  itemCount={groupItems.length}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleGroup(groupKey)}
                />

                {/* Group Items */}
                {!isCollapsed && (
                  <div className="space-y-0.5 pl-3">
                    {groupItems.map((item) =>
                      renderItem({
                        item,
                        isSelected: isSelected(config.getId(item)),
                        orderedIds,
                        onMultiSelectContextMenu,
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Ungrouped Items (when groupBy is "none") */}
      {hasContent && effectiveGroupBy === "none" && (
        <div className="space-y-0.5">
          {filteredItems.map((item) =>
            renderItem({
              item,
              isSelected: isSelected(config.getId(item)),
              orderedIds,
              onMultiSelectContextMenu,
            })
          )}
        </div>
      )}

      {/* No results message */}
      {!hasContent && searchTerm && (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">{noResultsMessage}</span>
        </div>
      )}
    </div>
  );
}

SidebarResourceSection.displayName = "SidebarResourceSection";

export default SidebarResourceSection;
